<template>
  <d-modal size="lg" @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('indentRegistry.addProductPopup.title') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form v-if="loadedProducts">
        <validation-observer ref="add-indent-product-form" tag="form">
          <div class="form-group">
            <label>
              {{ $t('product.productName') }}:
            </label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <d-row>
            <d-col cols="4">
              <div class="form-group">
                <label>
                    {{ $t('product.minimalQty') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="minimum_quantity"
                  v-slot="{ errors }">
                  <d-input-group :append="$t('ui.piecesAbbr')">
                    <d-input
                      type="number"
                      :min="1"
                      :step="1"
                      :state="errors.length ? 'invalid' : null"
                      v-model="minimum_quantity" />
                  </d-input-group>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group">
                <label>
                    {{ $t('product.piecesInBox') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="pieces_in_carton"
                  v-slot="{ errors }">
                  <d-input-group :append="$t('ui.piecesAbbr')">
                    <d-input
                      type="number"
                      :min="1"
                      :step="1"
                      :state="errors.length ? 'invalid' : null"
                      v-model="pieces_in_carton" />
                  </d-input-group>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group">
                <label>
                  {{ $t('product.actualPiecesInBox') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="real_pieces_in_carton"
                  v-slot="{ errors }">
                  <d-input-group :append="$t('ui.piecesAbbr')">
                    <d-input
                      type="number"
                      :min="1"
                      :step="1"
                      :state="errors.length ? 'invalid' : null"
                      v-model="real_pieces_in_carton" />
                  </d-input-group>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('product.productCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <d-checkbox
                  inline
                  v-model="include_discounts"
                  toggle
                  toggle-small>
                  {{ $t('product.includeDiscounts') }}
                </d-checkbox>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <d-checkbox
                  inline
                  v-model="include_in_services"
                  toggle
                  toggle-small>
                  {{ $t('product.includeInServices') }}
                </d-checkbox>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="6">
              <label>
                {{ $t('product.productIndentBaseProduct') }}:
              </label>

              <div class="form-group">
                <div :class="{ 'form-control': true, 'is-select': true }">
                  <v-select
                    class="w-100"
                    :options="products"
                    v-model="base_product_id"
                    :reduce="product => product.id"
                    :placeholder="$t('product.chooseProduct')"
                    label="code"
                    :filterBy="filterByForProducts">
                    <template slot="no-options">
                      {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center">
                        <template v-if="option.code">{{ option.code}} - </template>{{ option.name }}
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center">
                        <template v-if="option.code">{{ option.code}} - </template>{{ option.name }}
                      </div>
                    </template>
                  </v-select>
                </div>
              </div>
            </d-col>

            <d-col cols="6">
              <label>
                {{ $t('product.indentProduct.type') }}:
              </label>

              <d-button-group size="small" class="">
                <d-button
                  theme="primary"
                  :outline="type !== null"
                  @click.prevent="setIndentProductType(null)">
                  {{ $t('product.indentProduct.types.none') }}
                </d-button>
                <d-button
                  theme="primary"
                  :outline="type !== 'CAP'"
                  @click.prevent="setIndentProductType('CAP')">
                  {{ $t('product.indentProduct.types.cap') }}
                </d-button>
                <d-button
                  theme="primary"
                  :outline="type !== 'WINTER'"
                  @click.prevent="setIndentProductType('WINTER')">
                  {{ $t('product.indentProduct.types.winter') }}
                </d-button>
              </d-button-group>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="4">
              <div class="form-group">
                <label>
                  {{ $t('product.indentProduct.weight') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0|doubleVal"
                  name="weight"
                  v-slot="{ errors }">
                  <d-input-group :class="{'is-invalid': errors.length}">
                    <d-input
                      type="text"
                      class="text-right"
                      :state="errors.length ? 'invalid' : null"
                      v-model="weight" />
                    <d-input-group-text slot="append">
                      kg
                    </d-input-group-text>
                  </d-input-group>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group">
                <label>
                  {{ $t('product.indentProduct.cnCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="cn_code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="cn_code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group">
                <label>
                  {{ $t('product.indentProduct.originCountry') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="origin_country"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="origin_country" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <div class="form-group">
            <label>
              {{ $t('product.productDescription') }}:
            </label>

            <validation-provider
              rules="required"
              name="description"
              v-slot="{ errors }">
              <d-textarea
                :state="errors.length ? 'invalid' : null"
                v-model="description"
                :rows="8" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </validation-observer>
      </d-form>

      <div
        v-if="!loadedProducts && !loadError"
        class="text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show>
        {{ $t('indentRegistry.addProductPopup.loadingDataError') }}
      </d-alert>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        v-if="loadedProducts"
        class="ml-auto"
        theme="success"
        :disabled="saveInProgress"
        @click.prevent="validateForm">
        <div
          v-if="saveInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        {{ $t('indentRegistry.addProductPopup.addProduct') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'add-indent-product-popup',
  props: [
    'selectedProductIndex'
  ],
  data () {
    return {
      // fields
      name: '',
      minimum_quantity: 0,
      pieces_in_carton: 144,
      real_pieces_in_carton: 150,
      code: '',
      include_discounts: false,
      include_in_services: false,
      base_product_id: null,
      weight: '',
      cn_code: '',
      origin_country: 'CN',
      description: '',
      type: null,
      // helpers
      products: [],
      // ui flags
      loadError: false,
      loadedProducts: false,
      saveInProgress: false
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError',
        params: {
          where: {
            is_indent: 0
          }
        }
      });
    },
    handleClose () {
      this.$bus.$emit('close-add-indent-product-popup');
    },
    validateForm () {
      FormUtils.validate(this.$refs['add-indent-product-form'], this.addIndentProduct);
    },
    cancel () {
      this.handleClose();
    },
    filterByForProducts (option, label, search) {
      return (option.code + ' ' + option.price_code + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    addIndentProduct () {
      this.saveInProgress = true;

      let formDataToSend = {
        is_indent: 1,
        name: this.name,
        code: this.code,
        minimum_quantity: this.minimum_quantity,
        pieces_in_carton: this.pieces_in_carton,
        real_pieces_in_carton: this.real_pieces_in_carton,
        include_discounts: this.include_discounts,
        include_in_services: this.include_in_services,
        description: this.description,
        base_product_id: this.base_product_id,
        weight: Number(this.weight).toFixed(2),
        cn_code: this.cn_code,
        origin_country: this.origin_country,
        site_url: '',
        dropbox_url: '',
        type: this.type
      };

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/products/store',
        formData: formDataToSend,
        successTitle: this.$t('product.addProductSuccessTitle'),
        successText: this.$t('product.addProductSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.addProductErrorText'),
        successAction: (responseData) => {
          this.$bus.$emit('set-new-indent-product-in-order', responseData.id, this.selectedProductIndex);
          this.cancel();
        }
      });
    },
    setIndentProductType (type) {
      this.type = type;

      if (type === 'CAP') {
        this.weight = '0.08';
        this.cn_code = '65050030';
      } else if (type === 'WINTER') {
        this.weight = '0.09';
        this.cn_code = '65050090';
      }
    }
  }
}
</script>
